// SPDX-License-Identifier: AGPL-3.0-or-later
import { CONTRADICTION_VISIBILITY_DEFAULT, ContradictionRuleField, ExtensionUrl, } from '@intakeform/fhir';
import { countAnswers, firstAnswer } from './answers.js';
import { ExpressionError, evaluate, parse } from './expression.js';
/**
 * Cross-field pass: evaluate every `contradiction-rule` extension on the
 * Questionnaire against the response. A truthy result emits a `contradiction`
 * error finding. Expression syntax is the whitelisted language defined in
 * `expression.ts` — `answer(linkId)`, `count(linkId)`, comparisons, &&/||/!.
 *
 * Parse errors in builder-supplied expressions surface as form-level `info`
 * findings, not as `contradiction` errors, so a typo by the form author
 * does not block a patient submission.
 */
export function runCrossFieldPass(questionnaire, response) {
    const findings = [];
    const rules = (questionnaire.extension ?? []).filter((e) => e.url === ExtensionUrl.contradictionRule);
    const env = {
        answer: (linkId) => {
            const v = firstAnswer(response, linkId);
            return v === undefined ? null : v;
        },
        count: (linkId) => countAnswers(response, linkId),
    };
    for (const rule of rules) {
        const code = subString(rule, ContradictionRuleField.code) ?? 'contradiction';
        const message = subString(rule, ContradictionRuleField.message) ?? 'Contradictory answers.';
        const visibility = resolveVisibility(subString(rule, ContradictionRuleField.visibility));
        const label = subString(rule, ContradictionRuleField.label);
        const whenExt = subExtension(rule, ContradictionRuleField.when);
        const expression = whenExt?.valueExpression?.expression;
        if (expression === undefined) {
            findings.push({
                severity: 'info',
                code: 'contradiction',
                message: `Rule '${code}' has no 'when' expression and was skipped.`,
                context: { ruleCode: code, visibility },
            });
            continue;
        }
        let triggered;
        try {
            const ast = parse(expression);
            triggered = isTruthy(evaluate(ast, env));
        }
        catch (err) {
            const detail = err instanceof ExpressionError ? err.message : String(err);
            findings.push({
                severity: 'info',
                code: 'contradiction',
                message: `Rule '${code}' has an invalid expression and was skipped: ${detail}`,
                context: { ruleCode: code, visibility },
            });
            continue;
        }
        if (triggered) {
            findings.push({
                severity: 'error',
                code: 'contradiction',
                message,
                context: { ruleCode: code, visibility, ...(label !== undefined ? { label } : {}) },
            });
        }
    }
    return findings;
}
/**
 * Normalise the raw `visibility` sub-extension value. Anything other
 * than the literal string `'patient'` falls back to the safe default
 * (`clinician-only`). Unknown / typo'd values are treated as
 * clinician-only so a misconfigured rule never accidentally leaks to
 * the patient.
 */
function resolveVisibility(raw) {
    if (raw === 'patient')
        return 'patient';
    return CONTRADICTION_VISIBILITY_DEFAULT;
}
function subExtension(parent, url) {
    return (parent.extension ?? []).find((e) => e.url === url);
}
function subString(parent, url) {
    const ext = subExtension(parent, url);
    if (ext === undefined)
        return undefined;
    return ext.valueString ?? ext.valueCode;
}
function isTruthy(v) {
    if (v === null || v === undefined)
        return false;
    if (typeof v === 'boolean')
        return v;
    if (typeof v === 'number')
        return v !== 0 && !Number.isNaN(v);
    if (typeof v === 'string')
        return v.length > 0;
    return Boolean(v);
}
//# sourceMappingURL=cross-field.js.map